import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { T, card, cardTitle, tbl, th, td } from '../lib/theme'
import * as XLSX from 'xlsx'

const fmt = (n) => '₦' + (Number(n)||0).toLocaleString(undefined,{maximumFractionDigits:0})

function rag(pct) {
  if (pct >= 100) return { color:'#3fb950', bg:'rgba(63,185,80,.15)',  border:'rgba(63,185,80,.3)',  label:'Met'   }
  if (pct >= 70)  return { color:'#E8A020', bg:'rgba(232,160,32,.15)', border:'rgba(232,160,32,.3)', label:'Near'  }
  return              { color:'#f85149', bg:'rgba(248,81,73,.12)',  border:'rgba(248,81,73,.25)', label:'Below' }
}


export default function TargetBoard() {
  const [agents,      setAgents]      = useState([])
  const [overrides,   setOverrides]   = useState([])
  const [funding,     setFunding]     = useState([])  // wallet_funding rows
  const [targets,     setTargets]     = useState({})  // {class: amount}
  const [season,      setSeason]      = useState('dry')
  const [weeks,       setWeeks]       = useState([])
  const [filterWeek,  setFilterWeek]  = useState('')
  const [filterSA,    setFilterSA]    = useState('')
  const [filterLga,   setFilterLga]   = useState('')
  const [superAgents, setSuperAgents] = useState([])
  const [lgas,        setLgas]        = useState([])
  const [loading,     setLoading]     = useState(true)

  const selS = { fontSize:12, fontFamily:T.font, background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6, padding:'7px 12px',
    color:T.txtPrimary, cursor:'pointer' }

  const load = useCallback(async () => {
    const [
      { data:ag }, { data:wf_weeks },
      { data:tg }, { data:se },
      { data:sa }, { data:lg }, { data:ov }
    ] = await Promise.all([
      supabase.from('agents').select('id,agent_id,full_name,agent_class,super_agent_id,lga_id,lgas(name,tier),super_agents(company_name)').eq('status','Active').order('full_name'),
      supabase.from('wallet_funding').select('week_ending').order('week_ending',{ascending:false}).limit(5000),
      supabase.from('weekly_targets').select('*').order('effective_from',{ascending:false}).limit(10),
      supabase.from('app_settings').select('*'),
      supabase.from('super_agents').select('id,company_name').order('company_name'),
      supabase.from('lgas').select('id,name').order('name'),
      supabase.from('target_overrides').select('*').is('cleared_at', null),
    ])
    setAgents(ag||[])
    setOverrides(ov||[])
    const tm = {}
    ;(tg||[]).forEach(t => {
      if (!t.tier) return
      if (!tm[t.tier]) tm[t.tier] = {}
      if (!tm[t.tier][t.agent_class]) tm[t.tier][t.agent_class] = t.dry_season_target
    })
    setTargets(tm)
    if (se) { const s=se.find(x=>x.key==='season'); if(s) setSeason(s.value) }
    const uniqueWeeks = [...new Set((wf_weeks||[]).map(r=>r.week_ending))].sort((a,b)=>b.localeCompare(a))
    setWeeks(uniqueWeeks)
    if (uniqueWeeks[0]) setFilterWeek(uniqueWeeks[0])
    setSuperAgents(sa||[])
    setLgas(lg||[])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!filterWeek) return
    supabase.from('wallet_funding').select('*').eq('week_ending', filterWeek)
      .then(({ data }) => setFunding(data||[]))
  }, [filterWeek])

  // Build per-agent total funding (state + LGA summed)
  const agentFundingMap = {}
  funding.forEach(row => {
    const key = row.agent_id || row.agent_id_text
    if (!key) return
    agentFundingMap[key] = (agentFundingMap[key] || 0) + (row.amount_funded || row.amount || 0)
  })

  const seasonFactor = season === 'rain' ? 0.75 : 1.0

  // Build board rows — one row per agent
  const boardRows = agents
    .filter(a =>
      (!filterSA  || a.super_agent_id === filterSA) &&
      (!filterLga || a.lga_id === filterLga)
    )
    .map(a => {
      const agTier     = a.lgas?.tier || 'A'
      const saOverride  = overrides.find(o => o.super_agent_id === a.super_agent_id)
      const agentCount  = agents.filter(ag => ag.super_agent_id === a.super_agent_id).length || 1
      const baseTarget  = saOverride
        ? saOverride.override_amount / agentCount
        : (targets[agTier]?.[a.agent_class] || targets['A']?.[a.agent_class] || 120000) * seasonFactor
      const funded     = agentFundingMap[a.id] || agentFundingMap[a.agent_id] || 0
      const pct        = baseTarget > 0 ? Math.round((funded / baseTarget) * 100) : 0
      const met        = funded >= baseTarget
      return { ...a, baseTarget, funded, pct, met }
    })
    .sort((a,b) => b.pct - a.pct)

  // Summary KPIs
  const totalFunded  = boardRows.reduce((s,r) => s + r.funded, 0)
  const totalTarget  = boardRows.reduce((s,r) => s + r.baseTarget, 0)
  const metCount     = boardRows.filter(r=>r.met).length
  const overallPct   = totalTarget > 0 ? Math.round(totalFunded/totalTarget*100) : 0

  function exportExcel() {
    const rows = boardRows.map(r => ({
      'Week Ending':   filterWeek,
      'Agent ID':      r.agent_id||'',
      'Agent Name':    r.full_name,
      'LGA':           r.lgas?.name||'',
      'Super Agent':   r.super_agents?.company_name||'',
      'Class':         r.agent_class,
      'Weekly Target': r.baseTarget,
      'Total Funded':  r.funded,
      '% of Target':   r.pct + '%',
      'Status':        r.pct>=100?'Met':r.pct>=70?'Near':'Below',
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Target Board')
    XLSX.writeFile(wb, `TargetBoard_${filterWeek}.xlsx`)
  }

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading target board…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Weekly Target Board</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Agent-level target vs actual wallet funding · Green ≥100% Met · Amber 70–99% Near · Red &lt;70% Below
        </div>
      </div>

      {/* Summary KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total Agents',      value: boardRows.length,                        sub:'in view'              },
          { label:'Targets Met',        value: `${metCount} / ${boardRows.length}`,    sub:'agents on or above'   },
          { label:'Total Funded',       value: fmt(totalFunded),                        sub:`week of ${filterWeek}`, accent:true },
          { label:'Overall vs Target',  value: overallPct + '%',                        sub: overallPct>=100 ? '✓ Target met' : '✗ Below target',
            colour: overallPct>=100 ? '#3fb950' : '#f85149' },
        ].map(({ label,value,sub,colour,accent }) => (
          <div key={label} style={{ background:T.surface, borderRadius:10, border:`1px solid ${T.border}`, padding:'16px 20px' }}>
            <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, textTransform:'uppercase',
              letterSpacing:'.07em', marginBottom:8 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:800, lineHeight:1.1, marginBottom:4,
              color: colour || (accent ? T.blue : T.txtPrimary) }}>{value}</div>
            <div style={{ fontSize:10, color:T.txtMuted }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select style={selS} value={filterWeek} onChange={e=>setFilterWeek(e.target.value)}>
          {weeks.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
        <select style={selS} value={filterSA} onChange={e=>setFilterSA(e.target.value)}>
          <option value="">All Super Agents</option>
          {superAgents.map(sa => <option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
        </select>
        <select style={selS} value={filterLga} onChange={e=>setFilterLga(e.target.value)}>
          <option value="">All LGAs</option>
          {lgas.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <button style={{ padding:'7px 14px', background:'rgba(63,185,80,.15)', color:'#3fb950',
          border:'1px solid rgba(63,185,80,.25)', borderRadius:6, fontSize:11, fontWeight:700,
          fontFamily:T.font, cursor:'pointer' }} onClick={exportExcel}>
          ⬇ Export Excel
        </button>
        <span style={{ fontSize:11, color:T.txtMuted, marginLeft:'auto' }}>
          Season: <strong style={{ color: season==='dry'?T.amber:T.blue }}>
            {season==='dry'?'☀ Dry':'🌧 Rainy −25%'}</strong>
        </span>
      </div>

      {/* Table */}
      <div style={card}>
        <table style={tbl}>
          <thead><tr>
            {['#','Agent ID','Agent Name','LGA','Super Agent','Class','Weekly Target','Total Funded (State+LGA)','% of Target','Status'].map(h =>
              <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {boardRows.map((r,i) => (
              <tr key={r.id}>
                <td style={{ ...td, fontWeight:800, color: i<3?T.amber:T.txtMuted, width:28 }}>{i+1}</td>
                <td style={{ ...td, color:T.txtSecondary, fontFamily:'monospace', fontSize:11 }}>{r.agent_id||'—'}</td>
                <td style={{ ...td, fontWeight:600 }}>{r.full_name}</td>
                <td style={{ ...td, color:T.txtSecondary }}>{r.lgas?.name||'—'}</td>
                <td style={{ ...td, color:T.txtSecondary }}>{r.super_agents?.company_name||'—'}</td>
                <td style={{ ...td, color:T.txtSecondary, fontSize:10 }}>Cl.{r.agent_class}</td>
                <td style={{ ...td, color:T.txtSecondary }}>{fmt(r.baseTarget)}</td>
                <td style={{ ...td, fontWeight:700, color: rag(r.pct).color }}>{fmt(r.funded)}</td>
                <td style={td}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:80, height:6, background:T.surface3, borderRadius:3, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${Math.min(r.pct,100)}%`,
                        background: rag(r.pct).color, borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:12, fontWeight:700,
                      color: rag(r.pct).color }}>{r.pct}%</span>
                  </div>
                </td>
                <td style={td}>
                  <span style={{
                    display:'inline-block', padding:'2px 9px', borderRadius:20,
                    fontSize:9, fontWeight:700, letterSpacing:'.04em',
                    background: rag(r.pct).bg,
                    color:       rag(r.pct).color,
                    border:      `1px solid ${rag(r.pct).border}`,
                  }}>
                    {rag(r.pct).label}
                  </span>
                </td>
              </tr>
            ))}
            {boardRows.length===0 && (
              <tr><td colSpan={10} style={{ ...td, textAlign:'center', color:T.txtMuted }}>
                No data for this week. Upload wallet funding in the Scoring page first.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
